'use client';

import { useAnimate } from 'motion/react';
import { useEffect, useRef, useState } from 'react';
import SanityImage from '@/components/shared/SanityImage';
import useViewportSize from '@/hooks/useViewportSize';
import cn from '@/lib/cn';
import { VIEWPORT_SIZES } from '@/lib/constants';

function calcRelativeIndex(
  index: number,
  activeIndex: number,
  imagesLength: number,
) {
  return (index - activeIndex + imagesLength) % imagesLength >
    Math.floor(imagesLength / 2)
    ? ((index - activeIndex + imagesLength) % imagesLength) - imagesLength
    : (index - activeIndex + imagesLength) % imagesLength;
}

function getSlidesZIndex(relativeIndex: number) {
  switch (relativeIndex) {
    case -3:
      return 1;
    case -2:
      return 2;
    case -1:
      return 3;
    case 0:
      return 4;
    case 1:
      return 5;
    default:
      return 0;
  }
}

function getSlidesX(relativeIndex: number, containerWidth: number) {
  switch (relativeIndex) {
    case -1:
      return `${containerWidth * 0.1457}px`;
    case 0:
      return `${containerWidth * 0.36}px`;
    case 1:
      return `${containerWidth * 0.64}px`;
    case 2:
      return `${containerWidth * 0.8543}px`;
    default:
      return '0px';
  }
}

function getSlidesScale(relativeIndex: number) {
  switch (relativeIndex) {
    case -1:
    case 1:
      return 0.85;
    case 0:
      return 1;
    default:
      return 0.7;
  }
}

function getSlidesOpacity(relativeIndex: number) {
  if (relativeIndex > 0) return 0;

  return 1;
}

function Slide({
  containerWidth,
  image,
  imagesLength,
  index,
}: {
  containerWidth: number;
  image: NonNullable<PageCareerQueryResult>['jobs']['images'][number];
  index: number;
  imagesLength: number;
}) {
  const [scope, animate] = useAnimate();

  const activeIndex = useRef(2);
  const relativeIndex = useRef(calcRelativeIndex(index, 2, imagesLength));

  const [zIndex, setZIndex] = useState(getSlidesZIndex(relativeIndex.current));

  useEffect(() => {
    function step() {
      animate(
        scope.current,
        {
          opacity: getSlidesOpacity(relativeIndex.current),
          transform: `translateX(${getSlidesX(
            relativeIndex.current,
            containerWidth,
          )}) translateZ(0px) scale(${getSlidesScale(relativeIndex.current)})`,
        },
        {
          damping: 20,
          duration: 0.5,
          stiffness: 100,
          type: 'spring',
        },
      );

      setZIndex(getSlidesZIndex(relativeIndex.current));
    }

    step();

    const interval = setInterval(() => {
      activeIndex.current = (activeIndex.current + 1) % imagesLength;
      relativeIndex.current = calcRelativeIndex(
        index,
        activeIndex.current,
        imagesLength,
      );

      step();
    }, 4_000);
    return () => clearInterval(interval);
  }, [animate, containerWidth, imagesLength, index, scope]);

  return (
    <div
      ref={scope}
      className="absolute h-full w-1/5 origin-left"
      style={{
        zIndex,
      }}
    >
      <SanityImage
        className="h-full w-[30vw] max-w-none origin-left rounded-md object-cover lg:w-[24rem]"
        image={image}
        sizes={`(min-width: ${VIEWPORT_SIZES.lg}px) 24rem, 30vw`}
      />
    </div>
  );
}

export default function Slider({
  className = undefined,
  images,
}: {
  className?: string;
  images: NonNullable<PageCareerQueryResult>['jobs']['images'];
}) {
  const { width } = useViewportSize();
  const ref = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(0);

  useEffect(() => {
    if (!ref.current) return;

    setContainerWidth(ref.current.clientWidth);
  }, [width]);

  return (
    <div
      ref={ref}
      className={cn(
        'relative h-[36vw] overflow-hidden px-[16vw] md:px-[calc(19vw-4rem)] lg:h-[28.75rem] lg:px-0',
        className,
      )}
    >
      {images.map((image, i) => (
        <Slide
          key={image._key}
          image={image}
          index={i}
          imagesLength={images.length}
          containerWidth={containerWidth}
        />
      ))}
    </div>
  );
}
